import React, { useMemo } from "react";
import * as io from "io-ts";
import type { Project } from "../../../../../../api/useProjects";
import BlankStateV2 from "@ui/BlankStateV2";
import { DataProcessing } from "@ui/Illustration/react";
import t from "../../../../../../i18n";
import createGlobalState from "@ui/lib/createGlobalState";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import Section from "@ui/Section";
import TableCard, { HeaderRight } from "@ui/Table/Card";
import { styled } from "@linaria/react";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";
import { useTranslations } from "../../../../../../api/useTranslations";
import { nullable } from "diagnostics-lib/io";
import { FilterWrapper, NONE_LABEL, buildOptions, useSubStateSetter } from "@ui/Filter";
import FilterSelect, { type FilterOption } from "@ui/Filter/FilterSelect";
import Table from "./ClosedEventTable";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import Reset from "@ui/Filter/Reset";
import { Reparability } from "../../../../../../api/Reparability";
import Modal from "@ui/Modal";
import ExportEventsModalForm from "../../../components/TransactionalModals/ModalFormExportEvents";
import { useModals } from "@ui/lib/hooks/useModal";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DownloadLine } from "@ui/IconV2/react";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import { uniqBy } from "diagnostics-lib/array";
import { useSettings } from "../../../../../../api/useSettings";
import {
  aggregationIntervalFromPeriod,
  mapIntervalFromEntities,
  type AggregationPeriod,
} from "../../../../../../domain/aggregation/period";
import { useOutletContext } from "react-router";
import type { EventsOutletContext } from "..";
import { EventType, type Event } from "../../../../../../api/useEvents";
import { getEventDetection, getEventSystems } from "../../../../../../domain/event";
import useGroups from "../../../../../../api/useGroups";

interface P {
  project: Project;
  events: Event[];
}
const FilteredValuesByFilterType = io.strict({
  detection: io.array(nullable(io.string)),
  system: io.array(nullable(io.string)),
  reparability: io.array(nullable(Reparability)),
  type: io.array(EventType),
});
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;
const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  detection: [t("project.fault_events.tab.closed.detection", true)],
  system: [],
  reparability: [],
  type: [],
};

/** CreateGlobalState */
const useInputClosedEventsSearch = createGlobalState({
  storage: "session",
  key: "useInputClosedEventsSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});
const useClosedEventsFilters = createGlobalState({
  storage: "session",
  key: "useClosedEventsFilters",
  type: FilteredValuesByFilterType,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: true,
});

export const ClosedEventPage = () => {
  const { events, project, source } = useOutletContext<EventsOutletContext>();
  if (!project) throw new Error("no project");
  if (!source) throw new Error("no source");

  const closedEvents: Event[] = React.useMemo(
    () => events.filter(event => event.metadata.status === "CLOSED"),
    [events],
  );

  return <ClosedEventView project={project} events={closedEvents} />;
};

const ClosedEventView = ({ project, events }: P) => {
  const translations = useTranslations();
  const settings = useSettings();
  const settingsAggregationPeriod = settings.AGGREGATION_PERIOD as AggregationPeriod;
  const groups = useGroups(project.id);
  const aggregationInterval = useMemo(() => {
    if (settingsAggregationPeriod === "CYCLE" || settingsAggregationPeriod === "CYCLE_2") {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
        intervals: mapIntervalFromEntities(groups),
      });
    } else if (settingsAggregationPeriod === "ALL") {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
        limit: {
          from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
          to: new Date(),
        },
      });
    } else {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
      });
    }
  }, [
    groups,
    settingsAggregationPeriod,
    project.timezone,
    project.analysesAggregate?.earliestActiveAnalysis?.date,
  ]);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);
  const { modal, openModal, closeModal } = useModals<"export-events">();
  const [search, setSearch] = useInputClosedEventsSearch({ scope: project.id });
  const [eventFilters, setFaultEventFilters] = useClosedEventsFilters({
    scope: project.id,
  });

  const hiddenReparabilty = React.useMemo(
    () => events.every(e => e.reparability === null),
    [events],
  );

  const detectionSubStateSetter = useSubStateSetter(setFaultEventFilters, "detection");
  const systemSubStateSetter = useSubStateSetter(setFaultEventFilters, "system");
  const reparabilitySubStateSetter = useSubStateSetter(setFaultEventFilters, "reparability");
  const typeSubStateSetter = useSubStateSetter(setFaultEventFilters, "type");

  const detectionOptions = React.useMemo(
    () =>
      buildOptions(
        events
          .map(m => getEventDetection(m))
          .filter(detection => detection !== null)
          .map(detection => ({
            label: t("project.fault_events.tab.closed.detection", detection),
            value: t("project.fault_events.tab.closed.detection", detection),
          })),
      ),
    [events],
  );

  const systemOptions = React.useMemo(
    () =>
      buildOptions(
        events.flatMap<FilterOption<string | null>>(event => {
          const systemIds = getEventSystems(event);

          if (systemIds.length === 0) {
            return [{ label: NONE_LABEL, value: null }];
          }

          return systemIds.map(systemId => ({
            label: systemId,
            value: systemId,
          }));
        }),
      ),
    [events],
  );

  const reparabilityOptions = React.useMemo(
    () =>
      uniqBy(
        buildOptions(
          events.map(m => ({
            label: m.reparability ? t("reparability", m.reparability, translations) : NONE_LABEL,
            value: m.reparability ?? null,
          })),
        ),
        "value",
      ),
    [events, translations],
  );
  const typeOptions = React.useMemo(
    (): { label: string; value: EventType }[] => [
      { label: t("project.event.type.manual"), value: "MANUAL" },
      { label: t("project.event.type.diagnosed"), value: "DIAGNOSED" },
    ],
    [],
  );

  const filteredEvents = React.useMemo(
    () =>
      events.filter(event => {
        const reparabilityMatch =
          eventFilters.reparability.length === 0 ||
          eventFilters.reparability.includes(event.reparability);
        const typeMatch = settings.MANUAL_EVENTS_DEV_MODE_ENABLED
          ? eventFilters.type.length === 0 || eventFilters.type.includes(event.type)
          : true;
        const eventSystemIds = getEventSystems(event);
        const systemMatch =
          eventFilters.system.length === 0 ||
          eventSystemIds.some(systemId => eventFilters.system.includes(systemId));
        const detectionMatch =
          eventFilters.detection.length === 0 ||
          eventFilters.detection.includes(
            t("project.fault_events.tab.closed.detection", getEventDetection(event)),
          );
        return reparabilityMatch && systemMatch && detectionMatch && typeMatch;
      }),
    [
      events,
      eventFilters.reparability,
      eventFilters.system,
      eventFilters.detection,
      eventFilters.type,
      settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
    ],
  );

  const [filteredDatas, filteringInProgress] = useFilteredDatas(
    filteredEvents,
    search,
    e => e.name,
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const headerLeft = React.useMemo(
    () => (
      <>
        <SearchInputV2
          data-cy="search-fault-events"
          placeholder={t("commons.placeholder.search")}
          onChange={handleSearchChange}
          value={search}
        />
        <FilterWrapper>
          <FilterSelect
            title={t("project.fault_events.table.closed.metadata.detection")}
            filtered
            options={detectionOptions}
            values={eventFilters["detection"]}
            setValues={detectionSubStateSetter}
          />
          <FilterSelect<string | null>
            title={t("project.fault_events.table.live.metadata.system")}
            filtered
            options={systemOptions}
            values={eventFilters["system"]}
            setValues={systemSubStateSetter}
          />
          {!hiddenReparabilty && (
            <FilterSelect<Reparability | null>
              title={t("project.fault_events.table.live.metadata.reparability")}
              filtered
              options={reparabilityOptions}
              values={eventFilters["reparability"]}
              setValues={reparabilitySubStateSetter}
            />
          )}
          {settings.MANUAL_EVENTS_DEV_MODE_ENABLED && (
            <FilterSelect<EventType>
              title="Type"
              filtered
              options={typeOptions}
              values={eventFilters["type"]}
              setValues={typeSubStateSetter}
              hidden={typeOptions.length === 0}
            />
          )}
          <Reset
            filterValues={eventFilters}
            setFilterValues={setFaultEventFilters}
            defaultFilterValues={{
              detection: [],
              system: [],
              reparability: [],
              type: [],
            }}
          />
        </FilterWrapper>
      </>
    ),
    [
      search,
      handleSearchChange,
      eventFilters,
      setFaultEventFilters,
      systemOptions,
      reparabilityOptions,
      detectionOptions,
      detectionSubStateSetter,
      systemSubStateSetter,
      reparabilitySubStateSetter,
      hiddenReparabilty,
      typeSubStateSetter,
      typeOptions,
      settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
    ],
  );

  const headerRight = React.useMemo(
    () => (
      <TooltipV2
        placement="top"
        overlay={<OverlayWrapper text={t("project.fault_events.button.export")} />}
      >
        <ButtonIconV2 onClick={() => openModal("export-events")}>
          <DownloadLine size="m" />
        </ButtonIconV2>
      </TooltipV2>
    ),
    [openModal],
  );

  if (events.length === 0)
    return (
      <BlankStateV2
        title={t("project.fault_events.closed.blank_state.title")}
        subtitle={t("project.fault_events.closed.blank_state.subtitle")}
        picto={<DataProcessing size="l" />}
      />
    );
  return (
    <>
      <Section id="events-to-process" title="">
        <StyledTableCard
          title=""
          headerLeft={headerLeft}
          headerRight={canUserReadProject ? headerRight : undefined}
          className={TableCardWithoutBorder}
        >
          <Table project={project} events={filteredDatas} loading={filteringInProgress} />
        </StyledTableCard>
      </Section>
      <Modal
        title={t("project.fault_events.modal.title.export")}
        isOpen={modal === "export-events"}
        onRequestClose={closeModal}
      >
        <ExportEventsModalForm
          project={project}
          aggregationInterval={aggregationInterval}
          onRequestClose={closeModal}
        />
      </Modal>
    </>
  );
};

export default React.memo(ClosedEventView);

//#region CSS
const StyledTableCard = styled(TableCard)`
  ${HeaderRight} {
    column-gap: 8px;
  }
`;
//#endregion
